import React, {useState, useMemo, useContext, useRef, useEffect} from 'react';
import {Row, Col, Card, Badge, Button, Form, ButtonGroup, ListGroup, DropdownButton, Dropdown, Alert} from 'react-bootstrap';
import {Checkbox, RadioGroup, Radio} from 'rsuite';
import {TagsInput} from 'react-tag-input-component';
import Select from 'react-select';
import {MaterialReactTable, MRT_ToggleGlobalFilterButton} from 'material-react-table';
import {MRT_Localization_PT_BR} from 'material-react-table/locales/pt-BR';
import addDays from 'date-fns/addDays';
import Swal from 'sweetalert2';
import {decode as base64_decode, encode as base64_encode} from 'base-64';
import {Tooltip} from '@mui/material';
import axios from '../../services/axios';
import {EmpresaSelectorWithGroups, useEmpresaSelection} from '../../components/EmpresaSelector';
import {ConfigContext} from '../../contexts/ConfigContext';
import {mockTraceItems} from './mockData';
import './styles.scss';
import {
  formatDate,
  formatCurrency,
  formatCnpj,
  getExpirationBadge,
  getExpiredRowStyle,
  calculateSummary,
  exportToCSV,
  sortItems,
  daysDifference,
} from './utils';
import DateRangePeriodo from '../genericos/periodo';

// Default period value (moved outside component to avoid re-creation)
const getDefaultPeriod = () => [addDays(new Date(), -90), new Date()];

// Constants
const CHAVE_PREVIEW_LENGTH = 20;
const RastreabilidadePorLote = ({ empresaHook: empresaHookProp = null }) => {
  const configContext = useContext(ConfigContext);
  const sessaoRaw = sessionStorage.getItem('ddesp');
  const sessiosS = sessaoRaw ? JSON.parse(base64_decode(sessaoRaw)) : null;
  const usr = sessiosS?.[3];
  const userId = usr != null ? String(usr) : '0';

  // Quando empresaHookProp é fornecido pelo pai, deferInit=true evita fetch duplicado ao BD
  const internalEmpresaHook = useEmpresaSelection({
    userId,
    moduloAtual: configContext.state.moduloAtual,
    isMulti: true,
    deferInit: !!empresaHookProp,
  });

  const empresaHook = empresaHookProp ?? internalEmpresaHook;

  const {empselecionada, handleChangeEmpresa} = empresaHook;

  const empresasSelecionadas = Array.isArray(empselecionada)
    ? empselecionada
    : empselecionada
      ? [empselecionada]
      : [];

  // Altura da tela (similar ao tableNfe.js)
  const heightTela = window.innerHeight > 500 ? window.innerHeight - 300 : 500;

  // Ref para autoscroll dos filtros rápidos
  const quickFiltersRef = useRef(null);

  // Refs para capturar texto ainda não “commitado” nos TagsInput
  const lotesInputRef = useRef(null);
  const codigoProdutoInputRef = useRef(null);
  const descricaoProdutoInputRef = useRef(null);
  const numeroNfeInputRef = useRef(null);
  const chaveAcessoInputRef = useRef(null);

  // Filter states - Primary
  const [periodo, setPeriodo] = useState(getDefaultPeriod);
  const [lotes, setLotes] = useState([]);
  const [tipoBusca, setTipoBusca] = useState('exata');
  const [validadeProxima, setValidadeProxima] = useState(false);
  const [xDias, setXDias] = useState(30);
  
  // Filter states - Advanced (mais filtros)
  const [maisF, setMaisF] = useState(false);
  const [codigoProduto, setCodigoProduto] = useState([]);
  const [descricaoProduto, setDescricaoProduto] = useState([]);
  const [numeroNfe, setNumeroNfe] = useState([]);
  const [chaveAcesso, setChaveAcesso] = useState([]);

  // Table states
  const [rowSelection, setRowSelection] = useState({});

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Controla empty-state (diferenciar antes/depois da primeira busca)
  const [hasSearched, setHasSearched] = useState(false);

  // Filter states - Status
  const [statusFilter, setStatusFilter] = useState('todos'); // 'todos', 'vencidos', 'avencer', 'ok'
  const [textBtnFiltros, setTextBtnFiltros] = useState('+ Filtros');
  const [bgBtnFiltros, setBgBtnFiltros] = useState('success');
  const [empresaFiltro, setEmpresaFiltro] = useState(null);
  const [fixarPeriodo, setFixarPeriodo] = useState(() => localStorage.getItem(`periodoDoc${usr}`) !== null);
  const [ocultarFornecedor, setOcultarFornecedor] = useState(
    () => localStorage.getItem(`rastroLoteOcultarFornecedor${usr}`) === '1',
  );
  const [ocultarIntro, setOcultarIntro] = useState(
    () => localStorage.getItem(`rastroLoteIntroHidden${usr}`) === '1',
  );

  // Data states
  const [filteredData, setFilteredData] = useState([]);
  const [emitentes, setEmitentes] = useState([]);
  const [emitentesSelecionados, setEmitentesSelecionados] = useState([]);
  const [summary, setSummary] = useState({
    nfesEncontradas: 0,
    fornecedores: 0,
    valorTotal: 0,
    lotesAVencer: 0,
  });

  useEffect(() => {
    const item = localStorage.getItem(`periodoDoc${usr}`);
    if (item !== null) {
      try {
        const itemParse = JSON.parse(item);
        if (Array.isArray(itemParse) && itemParse.length >= 2) {
          const startDate = new Date(itemParse[0]);
          const endDate = new Date(itemParse[1]);
          setPeriodo([startDate, endDate]);
        }
      } catch (error) {
        localStorage.removeItem(`periodoDoc${usr}`);
        setFixarPeriodo(false);
      }
    }
  }, [usr]);

  // Extrai emitentes únicos dos dados
  const extrairEmitentes = (items) => {
    const emitentesMap = new Map();
    items.forEach(item => {
      const key = item.cnpjEmitente;
      if (!emitentesMap.has(key)) {
        emitentesMap.set(key, {
          cnpj: item.cnpjEmitente,
          nome: item.fornecedorNome,
          uf: item.ufEmitente,
        });
      }
    });
    return Array.from(emitentesMap.values()).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  };

  const dadosPorEmpresa = empresaFiltro
    ? filteredData.filter((item) => {
        const empresaId = empresaFiltro?.id ?? empresaFiltro?.value;
        if (empresaId != null && item?.empresaId != null) {
          return Number(item.empresaId) === Number(empresaId);
        }
        const cnpjFiltro = String(empresaFiltro?.cnpj || '').replace(/\D/g, '');
        if (cnpjFiltro) {
          const itemCnpj = String(item?.cnpjDestinatario || '').replace(/\D/g, '');
          return itemCnpj.includes(cnpjFiltro);
        }
        return true;
      })
    : filteredData;

  // Filtra dados por emitentes selecionados
  const dadosPorEmitente = emitentesSelecionados.length > 0
    ? dadosPorEmpresa.filter(item => emitentesSelecionados.some(em => em.cnpj === item.cnpjEmitente))
    : dadosPorEmpresa;

  // Filtra dados por status de validade
  const dadosPorStatus = statusFilter === 'todos' 
    ? dadosPorEmitente
    : dadosPorEmitente.filter(item => {
        if (!item.dVal) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expDate = new Date(item.dVal);
        expDate.setHours(0, 0, 0, 0);
        
        const daysUntilExpiration = daysDifference(expDate, today);
        
        if (statusFilter === 'vencidos') {
          return daysUntilExpiration < 0;
        } else if (statusFilter === 'avencer') {
          return daysUntilExpiration >= 0 && daysUntilExpiration <= xDias;
        } else if (statusFilter === 'ok') {
          return daysUntilExpiration > xDias;
        }
        return true;
      });

  const hasAnyInput = [lotes, codigoProduto, descricaoProduto, numeroNfe, chaveAcesso].some((value) => {
    if (Array.isArray(value)) {
      return value.some((item) => String(item || '').trim().length > 0);
    }
    return String(value || '').trim().length > 0;
  });

  const limpaCnpj = (v) => String(v || '').replace(/\D/g, '');

  const extrairCnpjsParaPesquisa = ({selecionadas = []}) => {
    const selecionadasArr = Array.isArray(selecionadas)
      ? selecionadas
      : selecionadas
        ? [selecionadas]
        : [];

    const cnpjs = selecionadasArr
      .map((e) => limpaCnpj(e?.cnpj_cpf ?? e?.cnpj ?? e?.cnpj_cpf_responsavel))
      .filter((v) => v && v.length >= 8);

    return Array.from(new Set(cnpjs));
  };

  const extractDownloadUrl = (responseData) => {
    if (!responseData) return '';
    if (typeof responseData === 'string') return responseData;
    if (typeof responseData === 'object') {
      return responseData.url || responseData.link || responseData.downloadUrl || '';
    }
    return '';
  };

  const isInvalidDownloadUrl = (url) => {
    return !url || url === '' || url === null || url === 'undefined';
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0];
    const [year, month, day] = dateOnly.split('-').map((num) => parseInt(num, 10));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const normalizeNfeKey = (maybeKey) => {
    const digits = String(maybeKey || '').replace(/\D/g, '');
    return digits.length === 44 ? digits : '';
  };

  const getRastroRowId = (row, index) => {
    const historyKey = String(row?.key ?? '').trim();
    const nItem = String(row?.nItem ?? '').trim();
    const chave = normalizeNfeKey(row?.chaveNfe);
    const lote = String(row?.lote ?? '').trim();

    // Precisa ser único por linha (uma NF-e pode ter vários itens)
    const base = [historyKey, nItem, chave, lote].filter((v) => v && String(v).length > 0).join('|');
    return base || String(index);
  };

  const periodoLabel = useMemo(() => {
    if (!Array.isArray(periodo) || !periodo?.[0] || !periodo?.[1]) return '';
    const fmt = new Intl.DateTimeFormat('pt-BR');
    return `${fmt.format(new Date(periodo[0]))} até ${fmt.format(new Date(periodo[1]))}`;
  }, [periodo]);

  const getPendingTagInputValue = (containerRef) => {
    const el = containerRef?.current;
    if (!el) return '';
    const input = el.querySelector('input');
    return input && typeof input.value === 'string' ? input.value : '';
  };

  const commitPendingTags = ({
    containerRef,
    currentTags,
    setTags,
    disabled,
  }) => {
    if (disabled) return Array.isArray(currentTags) ? currentTags : [];

    const pendingRaw = String(getPendingTagInputValue(containerRef) || '').trim();
    const safeCurrent = Array.isArray(currentTags) ? currentTags : [];
    if (!pendingRaw) return safeCurrent;

    const tokens = pendingRaw
      .split(',')
      .map((t) => String(t || '').trim())
      .filter((t) => t.length > 0);

    if (tokens.length === 0) return safeCurrent;

    const existing = new Set(safeCurrent.map((t) => String(t || '').trim()).filter((t) => t.length > 0));
    const merged = [...safeCurrent];
    tokens.forEach((t) => {
      if (!existing.has(t)) {
        existing.add(t);
        merged.push(t);
      }
    });

    if (merged.length !== safeCurrent.length) {
      setTags(merged);
    }

    // Limpa o input interno para evitar “texto pendente” após a busca
    const el = containerRef?.current;
    const input = el ? el.querySelector('input') : null;
    if (input && typeof input.value === 'string') {
      input.value = '';
    }

    return merged;
  };

  const summaryExtras = useMemo(() => {
    const uniqueLotes = new Set();
    const lotesVencidos = new Set();
    const lotesOk = new Set();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    (filteredData || []).forEach((item) => {
      const lote = String(item?.lote || '').trim();
      if (lote) uniqueLotes.add(lote);

      if (!lote || !item?.dVal) return;
      const expDate = parseLocalDate(item.dVal);
      if (!expDate) return;
      expDate.setHours(0, 0, 0, 0);
      const daysUntilExpiration = daysDifference(expDate, today);

      if (daysUntilExpiration < 0) {
        lotesVencidos.add(lote);
      } else if (daysUntilExpiration > xDias) {
        lotesOk.add(lote);
      }
    });

    const mediaPorNfe = summary?.nfesEncontradas > 0 ? (summary?.valorTotal || 0) / summary.nfesEncontradas : 0;

    return {
      itens: Array.isArray(filteredData) ? filteredData.length : 0,
      lotesUnicos: uniqueLotes.size,
      lotesVencidos: lotesVencidos.size,
      lotesOk: lotesOk.size,
      mediaPorNfe,
    };
  }, [filteredData, xDias, summary, daysDifference]);

  // Download de XMLs por lote (múltiplos XMLs do fornecedor selecionado)
  const handleDownloadXMLsPorLote = async () => {
    // Mantém consistência com o que o usuário está vendo (statusFilter aplicado)
    // Para o endpoint downloadLote, a API espera o "key" interno (record_id) como no Histórico.
    const keys = [...new Set((dadosPorStatus || []).map((item) => item?.key).filter(Boolean))];
    
    if (keys.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Nenhum item',
        html: 'Nenhuma NF-e disponível para download neste lote.',
      });
      return;
    }

    Swal.fire({
      title: 'Gerando arquivo ZIP',
      html: `Processando ${keys.length} NF-e(s)...`,
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const dataPost = [
        {
          original: '',
          key: keys,
          tipo: 0, // 0 = XML
          doc: 0,
          orig: 'lote',
          email: '',
        },
      ];

      const response = await axios.post('/api/historyNfe/', {dest: 'downloadLote', dataPost});
      const downloadUrl = extractDownloadUrl(response.data);

      Swal.close();

      if (isInvalidDownloadUrl(downloadUrl)) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Nenhum documento encontrado para download.',
        });
        return;
      }

      window.open(downloadUrl, '_blank');
      Swal.fire({
        icon: 'success',
        title: 'Download iniciado',
        text: `Arquivo ZIP com ${keys.length} XML(s) está sendo baixado!`,
        timer: 2500,
      });
    } catch (e) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Erro no download',
        text: e?.response?.data?.message || e?.message || 'Não foi possível baixar os arquivos',
      });
    }
  };

  // Download XML individual
  const handleDownloadXMLFromJSON = (xmlString, fileName) => {
    const xmlFileName = `${fileName}.xml`;
    const a = document.createElement('a');
    a.download = xmlFileName;
    a.href = window.URL.createObjectURL(new File([xmlString], xmlFileName, { type: 'text/xml' }));
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  // Visualizar DANFE (individual)
  const handleVisualizarDanfe = (item) => {
    if (!item || !item.chaveNfe || !item.cnpjEmitente) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao abrir DANFE',
        text: 'Item inválido',
      });
      return;
    }

    Swal.fire({
      title: 'Gerando DANFE',
      html: 'Estamos gerando o DANFE para você!',
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const dataPost = [item.chaveNfe, item.cnpjEmitente];
      axios
        .post('/api/historyNfe/', { dest: 'printDanfe', dataPost })
        .then((response) => {
          const danfeUrl = response.data;
          Swal.fire({
            title: 'Pronto!',
            html: 'O DANFE foi gerado com sucesso e será aberto em uma nova aba.',
            icon: 'success',
            allowOutsideClick: true,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          }).then(() => {
            window.open(danfeUrl, '_blank');
            Swal.hideLoading();
            Swal.close();
          });
        })
        .catch((error) => {
          Swal.hideLoading();
          Swal.fire({
            title: 'Ops!',
            html: 'Ocorreu um erro ao gerar o DANFE, tente novamente daqui a alguns minutos.',
            icon: 'error',
            allowOutsideClick: true,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          });
        });
    } catch (e) {
      Swal.hideLoading();
      Swal.fire({
        icon: 'error',
        title: 'Erro ao abrir DANFE',
        text: e?.message || 'Não foi possível gerar a DANFE',
      });
    }
  };

  // Perform search/filter
  const handleBuscar = async () => {
    if (isLoading) return; // Evita múltiplas requisições

    // Se o usuário digitou no TagsInput e clicou Buscar sem confirmar a tag (Enter),
    // fazemos o “commit” do texto pendente para garantir que o payload leve o filtro.
    const lotesForSearch = commitPendingTags({
      containerRef: lotesInputRef,
      currentTags: lotes,
      setTags: setLotes,
      disabled: validadeProxima,
    });
    const codigoProdutoForSearch = commitPendingTags({
      containerRef: codigoProdutoInputRef,
      currentTags: codigoProduto,
      setTags: setCodigoProduto,
      disabled: validadeProxima,
    });
    const descricaoProdutoForSearch = commitPendingTags({
      containerRef: descricaoProdutoInputRef,
      currentTags: descricaoProduto,
      setTags: setDescricaoProduto,
      disabled: validadeProxima,
    });
    const numeroNfeForSearch = commitPendingTags({
      containerRef: numeroNfeInputRef,
      currentTags: numeroNfe,
      setTags: setNumeroNfe,
      disabled: validadeProxima,
    });
    const chaveAcessoForSearch = commitPendingTags({
      containerRef: chaveAcessoInputRef,
      currentTags: chaveAcesso,
      setTags: setChaveAcesso,
      disabled: validadeProxima,
    });

    setHasSearched(true);
    
    setIsLoading(true);
    setEmitentesSelecionados([]);
    setEmpresaFiltro(null);
    setStatusFilter('todos');

    // Sempre limpar dados antigos ao iniciar uma nova busca
    setFilteredData([]);
    setEmitentes([]);
    setSummary({
      nfesEncontradas: 0,
      fornecedores: 0,
      valorTotal: 0,
      lotesAVencer: 0,
    });
    setRowSelection({});
    
    const filters = {
      empresasSelecionadas,
      periodo,
      lotes: lotesForSearch,
      tipoBusca,
      validadeProxima,
      xDias,
      codigoProduto: codigoProdutoForSearch,
      descricaoProduto: descricaoProdutoForSearch,
      numeroNfe: numeroNfeForSearch,
      chaveAcesso: chaveAcessoForSearch,
    };

    // Payload BE: manter padrão do sistema (empresa(s) via seletor global)
    const dataPost = {
      empresas: (empresasSelecionadas || []).map((e) => ({
        id: e?.value,
        cnpj: e?.cnpj_cpf,
        label: e?.label,
      })),
      // Padrão centralizado do sistema: CNPJs efetivos para pesquisa (backend não deve resolver via SELECT)
      cnpjsParaPesquisa: extrairCnpjsParaPesquisa({selecionadas: empresasSelecionadas}),
      periodo: Array.isArray(periodo)
        ? [
            periodo?.[0] ? new Date(periodo[0]).toISOString() : null,
            periodo?.[1] ? new Date(periodo[1]).toISOString() : null,
          ]
        : null,
      lotes: (lotesForSearch || []).join(','),
      tipoBusca,
      validadeProxima,
      xDias,
      codigoProduto: (codigoProdutoForSearch || []).join(','),
      descricaoProduto: (descricaoProdutoForSearch || []).join(','),
      numeroNfe: (numeroNfeForSearch || []).join(','),
      chaveAcesso: (chaveAcessoForSearch || []).join(','),
    };

    try {
      const resp = await axios.post('/api/rastroNfe/', {dest: 'pesqLotes', dataPost});

      // Extrai itens: resposta é {success: true, items: [...]}
      const items = resp?.data?.items && Array.isArray(resp.data.items)
        ? resp.data.items
        : [];

      if (items.length === 0) {
        // Monta mensagem com os filtros aplicados
        const filtrosAplicados = [];
        if (lotesForSearch && lotesForSearch.length > 0) filtrosAplicados.push(`Lotes: ${lotesForSearch.join(', ')}`);
        if (codigoProdutoForSearch && codigoProdutoForSearch.length > 0) filtrosAplicados.push(`Código Produto: ${codigoProdutoForSearch.join(', ')}`);
        if (descricaoProdutoForSearch && descricaoProdutoForSearch.length > 0) filtrosAplicados.push(`Descrição Produto: ${descricaoProdutoForSearch.join(', ')}`);
        if (numeroNfeForSearch && numeroNfeForSearch.length > 0) filtrosAplicados.push(`Número NF-e: ${numeroNfeForSearch.join(', ')}`);
        if (chaveAcessoForSearch && chaveAcessoForSearch.length > 0) filtrosAplicados.push(`Chave Acesso: ${chaveAcessoForSearch.join(', ')}`);
        if (validadeProxima) filtrosAplicados.push(`Vencimento próximo (${xDias} dias)`);

        const textoFiltros = filtrosAplicados.length > 0 
          ? `Filtros aplicados:\n\n${filtrosAplicados.join('\n')}\n\nTente ajustar os filtros e buscar novamente.`
          : 'Nenhum lote encontrado com os filtros aplicados.\n\nTente ajustar os filtros e buscar novamente.';

        Swal.fire({
          icon: 'info',
          title: 'Nenhum lote localizado',
          html: `<div>${escapeHtml(textoFiltros)}</div>`,
          confirmButtonText: 'OK',
        });
        setFilteredData([]);
        setEmitentes([]);
        setSummary({
          nfesEncontradas: 0,
          fornecedores: 0,
          valorTotal: 0,
          lotesAVencer: 0,
        });
      } else {
        // Backend aplica os filtros; no FE apenas ordenamos e calculamos métricas.
        const sorted = sortItems(items, validadeProxima ? 'validade' : 'emissao', validadeProxima ? 'asc' : 'desc');

        setFilteredData(sorted);
        setEmitentes(extrairEmitentes(sorted));
        setSummary(calculateSummary(sorted, xDias));
        
        // Autoscroll para os filtros rápidos
        setTimeout(() => {
          if (quickFiltersRef.current) {
            quickFiltersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
        
        // Aviso de sucesso
        // Swal.fire({
        //   icon: 'success',
        //   title: 'Busca realizada',
        //   text: `${sorted.length} registro(s) encontrado(s)`,
        //   timer: 2000,
        //   showConfirmButton: false,
        // });
      }
      
      setRowSelection({});
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao buscar',
        text: e?.message || 'Não foi possível consultar rastreabilidade agora.',
      });
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear filters
  const handleLimpar = () => {
    // Não limpa as empresas selecionadas nem os inputs de filtro.
    // Aqui, "Limpar" é apenas limpar o resultado (tabela/resumo/seleções).
    setEmitentesSelecionados([]);
    setEmpresaFiltro(null);
    setHasSearched(false);
    setStatusFilter('todos');
    setFilteredData([]);
    setEmitentes([]);
    setSummary({
      nfesEncontradas: 0,
      fornecedores: 0,
      valorTotal: 0,
      lotesAVencer: 0,
    });
    setRowSelection({});
  };

  // Download selected XMLs (múltiplos)
  const handleDownloadXMLs = async () => {
    const selectedKeys = Object.keys(rowSelection).filter((key) => rowSelection[key]);

    if (selectedKeys.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Nenhum item selecionado',
        text: 'Selecione ao menos um item para download',
      });
      return;
    }

    // Mapeia rowId -> linha original para extrair o "key" interno (record_id)
    const idToRow = new Map();
    (dadosPorStatus || []).forEach((row, idx) => {
      idToRow.set(getRastroRowId(row, idx), row);
    });

    const keys = [...new Set(selectedKeys.map((id) => idToRow.get(id)?.key).filter(Boolean))];

    if (keys.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Seleção inválida',
        text: 'Nenhum documento válido foi selecionado para download.',
      });
      return;
    }

    Swal.fire({
      title: 'Gerando arquivo ZIP',
      html: `Processando ${keys.length} NF-e(s)...`,
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const dataPost = [
        {
          original: '',
          key: keys,
          tipo: 0, // 0 = XML
          doc: 0,
          orig: 'lote',
          email: '',
        },
      ];

      const response = await axios.post('/api/historyNfe/', {dest: 'downloadLote', dataPost});
      const downloadUrl = extractDownloadUrl(response.data);

      Swal.close();

      if (isInvalidDownloadUrl(downloadUrl)) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Nenhum documento encontrado para download.',
        });
        return;
      }

      window.open(downloadUrl, '_blank');
      Swal.fire({
        icon: 'success',
        title: 'Download iniciado',
        text: `Arquivo ZIP com ${keys.length} XML(s) está sendo baixado!`,
        timer: 2500,
      });

      setRowSelection({});
    } catch (e) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Erro no download',
        text: e?.response?.data?.message || e?.message || 'Não foi possível baixar os arquivos',
      });
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Nenhum dado',
        text: 'Realize uma busca primeiro para exportar os dados',
      });
      return;
    }

    exportToCSV(filteredData, `rastreabilidade-lotes-${new Date().getTime()}.csv`);
  };

  // Abrir NF-e (detalhes)
  const handleAbrirNF = (item) => {
    if (!item || !item.chaveNfe || !item.cnpjEmitente) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao abrir DANFE',
        text: 'Item inválido',
      });
      return;
    }

    Swal.fire({
      title: 'Gerando DANFE',
      html: 'Estamos gerando o DANFE para você!',
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const dataPost = [item.chaveNfe, item.cnpjEmitente];
      axios
        .post('/api/historyNfe/', { dest: 'printDanfe', dataPost })
        .then((response) => {
          const danfeUrl = response.data;
          Swal.fire({
            title: 'Pronto!',
            html: 'O DANFE foi gerado com sucesso e será aberto em uma nova aba.',
            icon: 'success',
            allowOutsideClick: true,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          }).then(() => {
            window.open(danfeUrl, '_blank');
            Swal.hideLoading();
            Swal.close();
          });
        })
        .catch((error) => {
          Swal.hideLoading();
          Swal.fire({
            title: 'Ops!',
            html: 'Ocorreu um erro ao gerar o DANFE, tente novamente daqui a alguns minutos.',
            icon: 'error',
            allowOutsideClick: true,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          });
        });
    } catch (e) {
      Swal.hideLoading();
      Swal.fire({
        icon: 'error',
        title: 'Erro ao abrir DANFE',
        text: e?.message || 'Não foi possível gerar a DANFE',
      });
    }
  };

  // Baixar XML individual
  const handleBaixarXMLIndividual = (item) => {
    if (!item || !item.chaveNfe || !item.cnpjEmitente) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao gerar XML',
        text: 'Item inválido',
      });
      return;
    }

    Swal.fire({
      title: 'Gerando XML',
      html: 'Estamos gerando o XML para você!',
      icon: 'info',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const dataPost = [item.chaveNfe, item.cnpjEmitente];
      axios
        .post('/api/historyNfe/', { dest: 'downXML', dataPost })
        .then((response) => {
          const xmlContent = response.data;
          Swal.hideLoading();
          Swal.update({
            title: 'Pronto!',
            html: 'XML gerado com sucesso!<br/><br/><span class="f-14">Caso o download não tenha iniciado,<br/> verifique a permissão de pop-ups do seu navegador.</span>',
            icon: 'success',
            allowOutsideClick: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          });
          handleDownloadXMLFromJSON(xmlContent, item.chaveNfe);
        })
        .catch((error) => {
          Swal.hideLoading();
          Swal.fire({
            title: 'Ops!',
            html: 'Ocorreu um erro ao gerar o XML, tente novamente daqui a alguns minutos.',
            icon: 'error',
            allowOutsideClick: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#297BEA',
          });
        });
    } catch (e) {
      Swal.hideLoading();
      Swal.fire({
        icon: 'error',
        title: 'Erro ao gerar XML',
        text: e?.message || 'Não foi possível gerar o XML',
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        id: 'acao',
        header: 'Ação',
        size: 120,
        Cell: ({row}) => (
          <div>
            <ButtonGroup>
              <Tooltip title='Visualizar DANFE' arrow>
                <a
                  href='#!'
                  className='badge badge-light-success mr-2 pointer'
                  onClick={(e) => {
                    e.preventDefault();
                    handleVisualizarDanfe(row.original);
                  }}
                >
                  <i className='far fa-file f-12'></i>
                </a>
              </Tooltip>
              <Tooltip title='Baixar XML' arrow>
                <a
                  href='#!'
                  className='badge badge-light-primary mr-2 pointer'
                  onClick={(e) => {
                    e.preventDefault();
                    handleBaixarXMLIndividual(row.original);
                  }}
                >
                  <i className='fa fa-download f-12'></i>
                </a>
              </Tooltip>
            </ButtonGroup>
          </div>
        ),
      },
      {
        accessorKey: 'lote',
        header: 'Lote',
        size: 120,
      },
      {
        accessorKey: 'dVal',
        header: 'Validade',
        size: 150,
        Cell: ({cell}) => {
          const dVal = cell.getValue();
          const badge = getExpirationBadge(dVal, xDias);

          return (
            <div>
              <div>{formatDate(dVal)}</div>
              <Badge
                bg={badge.variant}
                className='mt-1'
              >
                {badge.text}
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: 'emissao',
        header: 'Emissão',
        size: 100,
        Cell: ({cell}) => formatDate(cell.getValue()),
      },
      {
        id: 'fornecedor',
        header: 'Fornecedor',
        size: 200,
        Cell: ({row}) => (
          <div>
            <div style={{fontSize: '12px', fontWeight: 'bold'}}>{formatCnpj(row.original.cnpjEmitente)}</div>
            <div style={{fontSize: '11px', color: '#666'}}>{row.original.fornecedorNome || '—'}</div>
          </div>
        ),
      },
      {
        id: 'nf',
        header: 'NF',
        size: 120,
        Cell: ({row}) => (
          <div>
            <div style={{fontSize: '12px'}}>
              {row.original.nNf}/{row.original.serie}
            </div>
            <Button
              size='sm'
              variant='link'
              className='rounded'
              style={{fontSize: '11px', padding: 0}}
              onClick={() => handleAbrirNF(row.original)}
            >
              Abrir NF
            </Button>
          </div>
        ),
      },
      {
        id: 'produto',
        header: 'Produto',
        size: 250,
        Cell: ({row}) => (
          <div>
            {(row.original.cProd || row.original.codigoProduto) && (
              <div style={{fontSize: '11px', color: '#666'}}>
                Cód: {row.original.cProd || row.original.codigoProduto}
              </div>
            )}
            <div style={{fontSize: '12px'}}>{row.original.xProd}</div>
            {row.original.ncm && <div style={{fontSize: '11px', color: '#666'}}>NCM: {row.original.ncm}</div>}
          </div>
        ),
      },
      {
        id: 'qtde',
        header: 'Qtde/Un',
        size: 100,
        Cell: ({row}) => (
          <div style={{textAlign: 'right'}}>
            {row.original.qCom} {row.original.uCom}
          </div>
        ),
      },
      {
        accessorKey: 'vProd',
        header: 'Valor',
        size: 110,
        Cell: ({cell}) => <div style={{textAlign: 'right'}}>{formatCurrency(cell.getValue())}</div>,
      },
      {
        id: 'cst',
        header: 'CST/ICMS',
        size: 120,
        Cell: ({row}) => (
          <div style={{fontSize: '11px'}}>
            {row.original.cstIcms && <div>CST: {row.original.cstIcms}</div>}
            {row.original.pIcms != null && <div>{row.original.pIcms}%</div>}
            {row.original.vIcms != null && <div>{formatCurrency(row.original.vIcms)}</div>}
          </div>
        ),
      },
    ],
    [xDias],
  );

  const normalizarEmpresaToolbar = (empresa) => {
    const cnpjRaw = empresa?.cnpj_cpf || empresa?.cnpj || empresa?.label?.split(' - ')[0] || '';
    const nome = empresa?.label?.split(' - ').slice(1).join(' - ') || empresa?.nome || '';
    const id = empresa?.value ?? empresa?.id;
    return {cnpj: cnpjRaw, nome, id};
  };

  return (
    <div className='rastreabilidade-por-lote'>
      {/* Filters Card */}
      <Card className='mb-3 shadow-sm'>
        {/* <Card.Header className='pb-2'>
          <Row>
            <Col sm={12}>
              <ListGroup
                as='ul'
                bsPrefix=' '
                className='breadcrumb'
              >
                <ListGroup.Item
                  as='li'
                  bsPrefix=' '
                  className='breadcrumb-item'
                >
                  <a
                    href='/#/history/nfe'
                    style={{textDecoration: 'none'}}
                  >
                    <i className='feather icon-home'></i>
                  </a>
                </ListGroup.Item>
                <ListGroup.Item
                  as='li'
                  bsPrefix=' '
                  className='breadcrumb-item'
                >
                  <a
                    href='/#/history/nfe'
                    style={{textDecoration: 'none', color: '#297BEA'}}
                  >
                    Histórico
                  </a>
                </ListGroup.Item>
                <ListGroup.Item
                  as='li'
                  bsPrefix=' '
                  className='breadcrumb-item'
                >
                  <a
                    href='/#/history/nfe'
                    style={{textDecoration: 'none', color: '#297BEA'}}
                  >
                    NF-e
                  </a>
                </ListGroup.Item>
                <ListGroup.Item
                  as='li'
                  bsPrefix=' '
                  className='breadcrumb-item active'
                >
                  Rastreio de lotes
                </ListGroup.Item>
              </ListGroup>
              <hr className='my-1' />
            </Col>
          </Row>
        </Card.Header> */}
        <div className='mt-3 ml-3'>
          <h6 className='text-muted mb-1'>
            <span className='mdi mdi-magnify mr-2'></span>Rastreabilidade por lote          </h6>
        </div>
        <Card.Body>
          {!ocultarIntro && (
            <div className='page-banner'>
              <div className='page-banner__top'>
                <div>
                  <h5 className='page-banner__title'>
                    <i className='mdi mdi-crosshairs-gps mr-2' />
                    Rastreio por Lote
                  </h5>
                  <p className='page-banner__desc'>Monitore a origem e a validade dos produtos a partir dos lotes informados nas NF-e. Identifique itens vencidos, a vencer e os fornecedores envolvidos, garantindo mais controle e segurança sobre sua operação.</p>
                </div>
                <button
                  type='button'
                  className='close page-banner__action'
                  aria-label='Fechar'
                  onClick={() => {
                    setOcultarIntro(true);
                    localStorage.setItem(`rastroLoteIntroHidden${usr}`, '1');
                  }}
                >
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
            </div>
          )}
          {/* PRIMARY FILTERS ROW 1 - Empresa e Período */}
          <Row className='mb-2'>
            <Col lg={5}>
              <EmpresaSelectorWithGroups
                hook={empresaHook}
                isMulti={true}
                disabled={false}
                showGruposButton={true}
                showModeToggle={true}
              />{' '}
              <div className='border-start ps-4'>
                <Checkbox
                  checked={validadeProxima}
                  onChange={(e, checked) => setValidadeProxima(checked)}
                >
                  <span className='fw-bold'>Lotes com validade próxima</span>
                </Checkbox>
                {validadeProxima && <div className='text-muted small mt-1'>Com esta opcao ativa, os filtros por lote/produto ficam desativados.</div>}
                {validadeProxima && (
                  <div className='mt-3 d-flex align-items-end gap-2'>
                    <div style={{minWidth: '200px'}}>
                      <Form.Label className='small text-muted mb-1'>Dias até vencimento</Form.Label>
                      <Form.Control
                        type='number'
                        min='1'
                        max='365'
                        value={xDias}
                        onChange={(e) => setXDias(parseInt(e.target.value, 10) || 30)}
                        size='sm'
                        style={{width: '120px'}}
                      />
                      <Badge
                        variant='light-success'
                        text='dark'
                        className='mt-1'
                      >
                        Filtro ativo
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <Col lg={3}>
              <Form.Label className='fw-bold mb-2'>Lote(s)</Form.Label>
              <div ref={lotesInputRef}>
                <TagsInput
                  value={lotes}
                  onChange={(values) => setLotes([...values])}
                  placeHolder='Insira os lotes (opcional)'
                  name='lotes'
                  disabled={validadeProxima}
                />
              </div>
              {validadeProxima && <Form.Text className='text-muted'>Desativado quando validade próxima estiver ativa.</Form.Text>}
            </Col>{' '}
            <Col lg={4}>
              <Form.Label className='fw-bold mb-2'>Período</Form.Label>
              <Tooltip
                title='Mantém o período salvo para ser utilizado nas próximas pesquisas.'
                arrow
                placement='top-start'
              >
                <span className='mdi mdi-information float-right text-primary pointer ml-lg-n2 ml-md-n2 ml-sm-n2 f-12'></span>
              </Tooltip>
              <Form.Check
                inline
                className=' ml-3 float-right'
                disabled={validadeProxima}
                checked={fixarPeriodo}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFixarPeriodo(checked);
                  if (checked) {
                    localStorage.setItem(`periodoDoc${usr}`, JSON.stringify(periodo));
                  } else {
                    localStorage.removeItem(`periodoDoc${usr}`);
                  }
                }}
                label='Fixar período'
                type='checkbox'
                id='checkPeriodo'
              />
              <div style={{opacity: validadeProxima ? 0.6 : 1, pointerEvents: validadeProxima ? 'none' : 'auto'}}>
                <DateRangePeriodo
                  periodo={periodo}
                  setPeriodo={setPeriodo}
                  exibeMaisFiltros={true}
                  setmaisF={setMaisF}
                  maisF={maisF}
                  textBtnFiltros={textBtnFiltros}
                  setTextBtnFiltros={setTextBtnFiltros}
                  bgBtnFiltros={bgBtnFiltros}
                  setBgBtnFiltros={setBgBtnFiltros}
                />
              </div>
              {validadeProxima && <Form.Text className='text-muted'>Periodo bloqueado enquanto validade próxima estiver ativa.</Form.Text>}
            </Col>
          </Row>

          {/* ADVANCED FILTERS - COLLAPSIBLE */}
          {maisF && (
            <>
              <hr className='my-4' />
              <div className='bg-light p-3 rounded-3 mb-4'>
                <h6 className='fw-bold mb-3'>Filtros Avançados</h6>
                <Row className='g-3'>
                  <Col
                    md={6}
                    lg={3}
                  >
                    <Form.Label className='small fw-bold'>Código do Produto / NCM</Form.Label>
                    <div ref={codigoProdutoInputRef}>
                      <TagsInput
                        value={codigoProduto}
                        onChange={(values) => setCodigoProduto([...values])}
                        placeHolder='Ex: 000123 ou 30049099'
                        name='codigoProduto'
                        disabled={validadeProxima}
                      />
                    </div>
                  </Col>
                  <Col
                    md={6}
                    lg={3}
                  >
                    <Form.Label className='small fw-bold'>Descrição do Produto</Form.Label>
                    <div ref={descricaoProdutoInputRef}>
                      <TagsInput
                        value={descricaoProduto}
                        onChange={(values) => setDescricaoProduto([...values])}
                        placeHolder='Digite a descrição'
                        name='descricaoProduto'
                        disabled={validadeProxima}
                      />
                    </div>
                  </Col>
                  <Col
                    md={6}
                    lg={3}
                  >
                    <Form.Label className='small fw-bold'>Número da NF-e</Form.Label>
                    <div ref={numeroNfeInputRef}>
                      <TagsInput
                        value={numeroNfe}
                        onChange={(values) => setNumeroNfe([...values])}
                        placeHolder='Ex: 12345'
                        name='numeroNfe'
                        disabled={validadeProxima}
                      />
                    </div>
                  </Col>
                  <Col
                    md={6}
                    lg={3}
                  >
                    <Form.Label className='small fw-bold'>Chave de Acesso</Form.Label>
                    <div ref={chaveAcessoInputRef}>
                      <TagsInput
                        value={chaveAcesso}
                        onChange={(values) => setChaveAcesso([...values])}
                        placeHolder='44 dígitos'
                        name='chaveAcesso'
                        disabled={validadeProxima}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
          {/* PRIMARY FILTERS ROW 2 - Busca e Validade */}
          <Row className=' justify-content-center align-items-center'>
            {!validadeProxima && hasAnyInput && (
              <Col
                sm={3}
                className='text-center d-flex flex-column align-items-center'
              >
                <Form.Label className='fw-bold mb-3'>Tipo de Busca</Form.Label>
                <RadioGroup
                  name='tipoBusca'
                  className='text-center'
                  value={tipoBusca}
                  onChange={(value) => setTipoBusca(value)}
                  disabled={validadeProxima}
                  inline
                >
                  <Radio
                    value='exata'
                    className='mr-3'
                  >
                    Exata
                  </Radio>
                  <Radio
                    value='contem'
                    className='mr-3'
                  >
                    Contém
                  </Radio>
                  <Radio value='inicia'>Inicia com</Radio>
                </RadioGroup>
              </Col>
            )}
          </Row>
          {/* ACTION BUTTONS */}
          <Card.Footer className=''>
            <Row className='g-2 justify-content-center'>
              <Col className='text-center'>
                <Button
                  variant='link'
                  size='sm'
                  className='rounded'
                  onClick={handleLimpar}
                  disabled={isLoading}
                >
                  <i className='mdi mdi-refresh mr-2'></i>Limpar
                </Button>

                <Button
                  variant='primary'
                  className='rounded ml-3'
                  size='sm'
                  onClick={handleBuscar}
                  disabled={empresasSelecionadas.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className='mdi mdi-loading mdi-spin mr-2'></i>
                      Buscando...
                    </>
                  ) : (
                    <>
                      <i className='mdi mdi-magnify mr-2'></i>
                      Buscar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Footer>
        </Card.Body>
      </Card>

      {/* Summary Cards */}
      {filteredData.length > 0 && (
        <Row className='justify-content-center mb-0'>
          <Col
            sm={6}
            lg={3}
          >
            <Card className='summary-card mb-3 summary-card--nfe'>
              <Card.Body>
                <div className='summary-card__row'>
                  <div className='summary-card__text'>
                    <div className='d-flex align-items-start justify-content-between gap-2'>
                      <div className='summary-label mb-0'>NF-es encontradas</div>
                    </div>
                    <div className='summary-value'>{summary.nfesEncontradas}</div>
                    {periodoLabel && <div className='summary-meta'>Período: {periodoLabel}</div>}

                    <div className='mt-2 pt-2 border-top'>
                      <div className='summary-detail-row small text-muted'>
                        <span className='summary-detail-label'>Itens</span> <span className='summary-detail-value fw-bold text-dark'>{summaryExtras.itens}</span>
                      </div>
                      <div className='summary-detail-row small text-muted mt-1'>
                        <span className='summary-detail-label'>Lotes únicos</span> <span className='summary-detail-value fw-bold text-dark'>{summaryExtras.lotesUnicos}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className='summary-card__icon'
                    aria-hidden
                  >
                    <i className='mdi mdi-file-document-multiple-outline'></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            sm={6}
            lg={3}
          >
            <Card className='summary-card mb-3 summary-card--forn'>
              <Card.Body>
                <div className='summary-card__row'>
                  <div className='summary-card__text'>
                    <div className='summary-label'>Fornecedores</div>
                    <div className='summary-value'>{summary.fornecedores}</div>
                    <div className='mt-2 pt-2 border-top'>
                      <div className='summary-detail-row small text-muted'>
                        <span className='summary-detail-label'>Filtro</span> <span className='summary-detail-value fw-bold text-dark'>{emitentesSelecionados.length > 0 ? `${emitentesSelecionados.length} selecionado(s)` : 'nenhum'}</span>
                      </div>
                      <div className='summary-detail-row small text-muted mt-1'>
                        <span className='summary-detail-label'>Emitentes listados</span> <span className='summary-detail-value fw-bold text-dark'>{emitentes.length}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className='summary-card__icon'
                    aria-hidden
                  >
                    <i className='mdi mdi-account-multiple-outline'></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            sm={6}
            lg={3}
          >
            <Card className='summary-card mb-3 summary-card--valor'>
              <Card.Body>
                <div className='summary-card__row'>
                  <div className='summary-card__text'>
                    <div className='summary-label'>Valor total</div>
                    <div className='summary-value'>{formatCurrency(summary.valorTotal)}</div>
                    <div className='mt-2 pt-2 border-top'>
                      <div className='summary-detail-row small text-muted'>
                        <span className='summary-detail-label'>Média por NF-e</span> <span className='summary-detail-value fw-bold text-dark'>{formatCurrency(summaryExtras.mediaPorNfe)}</span>
                      </div>
                      <div className='summary-detail-row small text-muted mt-1'>
                        <span className='summary-detail-label'>Registros</span> <span className='summary-detail-value fw-bold text-dark'>{filteredData.length}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className='summary-card__icon'
                    aria-hidden
                  >
                    <i className='mdi mdi-cash-multiple'></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col
            sm={6}
            lg={3}
          >
            <Card className='summary-card mb-3 summary-card--avencer'>
              <Card.Body>
                <div className='summary-card__row'>
                  <div className='summary-card__text'>
                    <div className='summary-label'>Lotes a vencer (≤ {xDias} dias)</div>
                    <div className='summary-value text-warning'>{summary.lotesAVencer}</div>
                    <div className='mt-2 pt-2 border-top'>
                      <div className='summary-detail-row small text-muted'>
                        <span className='summary-detail-label'>Vencidos</span> <span className='summary-detail-value fw-bold text-dark'>{summaryExtras.lotesVencidos}</span>
                      </div>
                      <div className='summary-detail-row small text-muted mt-1'>
                        <span className='summary-detail-label'>OK</span> <span className='summary-detail-value fw-bold text-dark'>{summaryExtras.lotesOk}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className='summary-card__icon'
                    aria-hidden
                  >
                    <i className='mdi mdi-alert-circle-outline'></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Quick Filters */}
      {filteredData.length > 0 && (
        <Card
          className='mb-3'
          ref={quickFiltersRef}
        >
          <Card.Body>
            <Row className='mb-2'>
              <Col md={12}>
                <h6 className='text-muted mb-3'>
                  <i className='mdi mdi-calendar-alert mr-2'></i>
                  Filtro rápido por Status de Validade
                </h6>
              </Col>
            </Row>
            <Row>
              <Col
                md={12}
                className='d-flex gap-2 flex-wrap'
              >
                <div
                  className={`mr-2 badge p-2 cursor-pointer ${statusFilter === 'todos' ? 'bg-primary text-white' : 'bg-light text-dark border'}`}
                  onClick={() => setStatusFilter('todos')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                  title='Mostrar todos os itens'
                >
                  <i className='mdi mdi-checkbox-marked-circle-outline mr-1'></i>
                  Todos ({dadosPorEmitente.length})
                </div>
                <div
                  className={`mr-2 badge p-2 cursor-pointer ${statusFilter === 'vencidos' ? 'bg-danger text-white' : 'bg-light text-dark border'}`}
                  onClick={() => setStatusFilter('vencidos')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                  title='Mostrar itens vencidos'
                >
                  <i className='mdi mdi-close-circle-outline mr-1'></i>
                  Vencidos
                </div>
                <div
                  className={`mr-2 badge p-2 cursor-pointer ${statusFilter === 'avencer' ? 'bg-warning text-dark' : 'bg-light text-dark border'}`}
                  onClick={() => setStatusFilter('avencer')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                  title={`Mostrar itens a vencer em até ${xDias} dias`}
                >
                  <i className='mdi mdi-alert-circle-outline mr-1'></i>A vencer (≤ {xDias}d)
                </div>
                <div
                  className={`badge p-2 cursor-pointer ${statusFilter === 'ok' ? 'bg-success text-white' : 'bg-light text-dark border'}`}
                  onClick={() => setStatusFilter('ok')}
                  style={{cursor: 'pointer', userSelect: 'none'}}
                  title={`Mostrar itens com validade maior que ${xDias} dias`}
                >
                  <i className='mdi mdi-check-circle-outline mr-1'></i>
                  Válidos ({`>${xDias}d`})
                </div>
              </Col>
            </Row>
            {emitentes.length > 0 && (
              <>
                <hr className='my-3' />
                <Row className='mb-2'>
                  <Col
                    md={12}
                    className='d-flex align-items-center justify-content-between flex-wrap gap-2'
                  >
                    <h6 className='text-muted mb-3'>
                      <i className='mdi mdi-filter-multiple mr-2'></i>
                      Filtros rápidos por Fornecedor
                    </h6>
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      className='rounded'
                      onClick={() => {
                        const next = !ocultarFornecedor;
                        setOcultarFornecedor(next);
                        localStorage.setItem(`rastroLoteOcultarFornecedor${usr}`, next ? '1' : '0');
                      }}
                    >
                      {ocultarFornecedor ? 'Mostrar' : 'Ocultar'}
                    </Button>
                  </Col>
                </Row>
                {!ocultarFornecedor && (
                  <Row className='mb-3'>
                    <Col md={4}>
                      <Form.Label>Selecionar Fornecedores</Form.Label>
                      <Select
                        options={emitentes.map((emitente) => ({
                          value: emitente.cnpj,
                          label: `${formatCnpj(emitente.cnpj)} - ${emitente.nome} (${emitente.uf})`,
                          emitente,
                        }))}
                        value={emitentesSelecionados.map((em) => ({
                          value: em.cnpj,
                          label: `${formatCnpj(em.cnpj)} - ${em.nome} (${em.uf})`,
                          emitente: em,
                        }))}
                        onChange={(options) => {
                          if (options && options.length > 0) {
                            setEmitentesSelecionados(options.map((opt) => opt.emitente));
                          } else {
                            setEmitentesSelecionados([]);
                          }
                        }}
                        isMulti
                        isSearchable
                        isClearable
                        placeholder='Selecione um ou mais fornecedores...'
                        noOptionsMessage={() => 'Nenhum fornecedor encontrado'}
                        styles={{
                          control: (base) => ({
                            ...base,
                            fontSize: '14px',
                            borderRadius: '4px',
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: '13px',
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </Col>
                    <Col
                      md={8}
                      className='d-flex align-items-end gap-3'
                    >
                      {emitentesSelecionados.length > 0 && (
                        <>
                          <Button
                            variant='info'
                            size='sm'
                            className='rounded mr-2'
                            onClick={handleDownloadXMLsPorLote}
                            title='Baixar todos os XMLs dos fornecedores selecionados'
                          >
                            <i className='mdi mdi-download mr-1'></i>
                            Baixar lote ({dadosPorStatus.length} items)
                          </Button>
                          <Button
                            variant='outline-info'
                            size='sm'
                            className='rounded mr-2'
                            onClick={() => {
                              const chaves = [...new Set(dadosPorStatus.map((item) => item.chaveNfe))];
                              Swal.fire({
                                icon: 'info',
                                title: 'NF-es dos fornecedores',
                                text: `${chaves.length} NF-e(s) encontrada(s)`,
                              });
                            }}
                          >
                            <i className='mdi mdi-information mr-1'></i>
                            Info
                          </Button>
                        </>
                      )}
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        className='rounded'
                        onClick={() => setEmitentesSelecionados([])}
                        disabled={emitentesSelecionados.length === 0}
                      >
                        <i className='mdi mdi-close mr-1'></i>
                        Limpar filtro
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Results Grid */}
      {filteredData.length > 0 && (
        <Card>
          <Card.Body>
            {/* Action buttons above table */}
            <Row className='mb-3'>
              <Col>
                <Button
                  variant='success'
                  onClick={handleDownloadXMLs}
                  disabled={Object.keys(rowSelection).filter((key) => rowSelection[key]).length === 0}
                  className='mr-2 rounded'
                >
                  <i className='mdi mdi-download mr-1'></i>
                  Baixar XML selecionados ({Object.keys(rowSelection).filter((key) => rowSelection[key]).length})
                </Button>
                <Button
                  variant='outline-secondary'
                  className='rounded'
                  onClick={handleExportCSV}
                >
                  <i className='mdi mdi-file-excel mr-1'></i>
                  Exportar CSV
                </Button>
              </Col>
            </Row>

            {/* Table */}
            <MaterialReactTable
              columns={columns}
              data={dadosPorStatus}
              localization={MRT_Localization_PT_BR}
              getRowId={(row, index) => getRastroRowId(row, index)}
              enableRowSelection
              enableSelectAll
              enableGlobalFilter
              onRowSelectionChange={setRowSelection}
              state={{rowSelection}}
              enableColumnResizing
              enableStickyHeader
              enableStickyFooter
              enablePagination
              initialState={{
                pagination: {pageSize: 10, pageIndex: 0},
                density: 'compact',
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: `${heightTela}px`,
                },
              }}
              muiTableProps={{
                sx: {
                  fontSize: '13px',
                  fontFamily: '"Nunito", sans-serif',
                  color: '#666',
                },
              }}
              muiTableBodyProps={{
                sx: {
                  fontSize: {
                    xs: '10px',
                    sm: '11px',
                    md: '12px',
                    lg: '13px',
                    xl: '13px',
                  },
                  color: '#666',
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: {
                    xs: '10px',
                    sm: '11px',
                    md: '12px',
                    lg: '13px',
                    xl: '13px',
                  },
                  fontFamily: '"Nunito", sans-serif',
                  color: '#666',
                  fontWeight: 'bold',
                  backgroundColor: '#f8f9fa',
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  fontSize: {
                    xs: '10px',
                    sm: '11px',
                    md: '12px',
                    lg: '13px',
                    xl: '13px',
                  },
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  fontFamily: '"Nunito", sans-serif',
                  color: '#666',
                },
              }}
              muiTablePaperProps={{
                sx: {
                  fontSize: {
                    xs: '10px',
                    sm: '11px',
                    md: '12px',
                    lg: '13px',
                    xl: '13px',
                  },
                },
              }}
              muiTableBodyRowProps={({row}) => ({
                sx: {
                  ...getExpiredRowStyle(row.original.dVal),
                },
              })}
              renderTopToolbarCustomActions={() => {
                const empresasArray = (empresasSelecionadas || []).map((empresa) => normalizarEmpresaToolbar(empresa)).filter((empresa) => empresa.cnpj || empresa.nome || empresa.id != null);

                if (empresasArray.length === 0) return null;

                if (empresasArray.length <= 2) {
                  return (
                    <div className='d-flex align-items-center flex-wrap gap-2'>
                      <span className='text-muted small mr-1'>Empresas:</span>
                      {empresasArray.map((empresa, index) => {
                        const isAtivo = empresaFiltro ? (empresaFiltro.id != null && empresa.id != null ? Number(empresaFiltro.id) === Number(empresa.id) : empresaFiltro.cnpj === empresa.cnpj) : false;
                        const label = `${formatCnpj(empresa.cnpj) || empresa.cnpj}${empresa.nome ? ` - ${empresa.nome}` : ''}`;

                        return (
                          <Badge
                            key={`empresa-chip-${index}-${empresa.id || empresa.cnpj}`}
                            variant={isAtivo ? 'primary' : 'light'}
                            className='f-12 mb-0 f-w-400 mr-2'
                            style={{
                              cursor: 'pointer',
                              border: isAtivo ? '2px solid #297BEA' : '1px solid #ddd',
                            }}
                            onClick={() => {
                              if (isAtivo) {
                                setEmpresaFiltro(null);
                              } else {
                                setEmpresaFiltro({
                                  cnpj: empresa.cnpj,
                                  nome: empresa.nome,
                                  id: empresa.id,
                                });
                              }
                            }}
                            title={isAtivo ? 'Clique para remover filtro' : 'Clique para filtrar apenas esta empresa'}
                          >
                            {label}
                          </Badge>
                        );
                      })}
                    </div>
                  );
                }

                const empresaAtualTexto = empresaFiltro ? `${formatCnpj(empresaFiltro.cnpj) || empresaFiltro.cnpj} - ${empresaFiltro.nome || ''}` : `${empresasArray.length} empresas`;

                return (
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <span className='text-muted small mr-1'>Empresas:</span>
                    <DropdownButton
                      id='dropdown-empresas-toolbar'
                      title={
                        <span className='py-0'>
                          <i className='feather icon-briefcase mr-1'></i>
                          {empresaAtualTexto}
                        </span>
                      }
                      variant={empresaFiltro ? 'primary' : 'light'}
                      className='d-inline-block mr-2 py-0'
                      size='sm'
                    >
                      {empresaFiltro && (
                        <>
                          <Dropdown.Item
                            onClick={() => setEmpresaFiltro(null)}
                            className='dropdown-item-remover'
                            style={{fontSize: '12px', color: '#ff5252'}}
                          >
                            <i className='feather icon-x mr-1'></i>
                            Remover filtro
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      )}
                      <Dropdown.Header style={{fontSize: '11px', fontWeight: 'bold'}}>Filtrar por empresa:</Dropdown.Header>
                      {empresasArray.map((empresa, index) => {
                        const isAtivo = empresaFiltro ? (empresaFiltro.id != null && empresa.id != null ? Number(empresaFiltro.id) === Number(empresa.id) : empresaFiltro.cnpj === empresa.cnpj) : false;
                        return (
                          <Dropdown.Item
                            key={`empresa-dropdown-${index}-${empresa.id || empresa.cnpj}`}
                            onClick={() =>
                              setEmpresaFiltro({
                                cnpj: empresa.cnpj,
                                nome: empresa.nome,
                                id: empresa.id,
                              })
                            }
                            className='dropdown-item-empresa'
                            style={{
                              fontSize: '12px',
                              backgroundColor: isAtivo ? '#e3f2fd' : 'transparent',
                              fontWeight: isAtivo ? 'bold' : 'normal',
                              color: '#333',
                            }}
                          >
                            {isAtivo && (
                              <i
                                className='feather icon-check mr-1'
                                style={{color: '#297BEA'}}
                              ></i>
                            )}
                            {formatCnpj(empresa.cnpj) || empresa.cnpj}
                            {empresa.nome ? ` - ${empresa.nome}` : ''}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </div>
                );
              }}
              renderToolbarInternalActions={({table}) => (
                <>
                  <MRT_ToggleGlobalFilterButton table={table} />
                </>
              )}
            />
          </Card.Body>
        </Card>
      )}

      {/* Empty state */}
      {filteredData.length === 0 && empresasSelecionadas.length > 0 && (
        <Card>
          <Card.Body className='text-center py-5'>
            <i
              className='mdi mdi-magnify'
              style={{fontSize: '48px', color: '#ccc'}}
            ></i>
            <p className='mt-3 text-muted'>{hasSearched ? 'Nenhum registro localizado com os filtros informados.' : 'Clique em "Buscar" para pesquisar lotes nas NF-es recebidas'}</p>
          </Card.Body>
        </Card>
      )}

      {empresasSelecionadas.length === 0 && (
        <Card>
          <Card.Body className='text-center py-5'>
            <i
              className='mdi mdi-alert-circle-outline'
              style={{fontSize: '48px', color: '#ffc107'}}
            ></i>
            <p className='mt-3 text-muted'>Selecione ao menos uma empresa para iniciar a busca</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default RastreabilidadePorLote;
